import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getToken, clearToken } from '../services/tokenService';
import { Heart, LogOut, Building2, MapPin, Layers, Loader2, AlertTriangle, Users,
  LayoutDashboard, ScrollText, HandCoins, BarChart3, Rocket, Network, CalendarDays, RefreshCw } from 'lucide-react';
import ConventionDocumentsTab from './espace-sponsor/ConventionDocumentsTab';
import ContributionsTab from './espace-sponsor/ContributionsTab';
import ImpactTab from './espace-sponsor/ImpactTab';
import ProgrammesTab from './espace-sponsor/ProgrammesTab';
import ReseauTab from './espace-sponsor/ReseauTab';
import EvenementsTab from './espace-sponsor/EvenementsTab';
import RenouvellementTab from './espace-sponsor/RenouvellementTab';

const API = process.env.REACT_APP_BACKEND_URL;

const TABS = [
  { id: 'dashboard', label: 'Tableau de bord', icon: LayoutDashboard },
  { id: 'convention', label: 'Convention & Documents', icon: ScrollText },
  { id: 'contributions', label: 'Contributions', icon: HandCoins },
  { id: 'impact', label: 'Impact', icon: BarChart3 },
  { id: 'programmes', label: 'Programmes', icon: Rocket },
  { id: 'reseau', label: 'Réseau', icon: Network },
  { id: 'evenements', label: 'Événements', icon: CalendarDays },
  { id: 'renouvellement', label: 'Renouvellement', icon: RefreshCw },
];

export default function EspaceSponsorPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [role, setRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tab, setTab] = useState('dashboard');

  useEffect(() => {
    (async () => {
      const t = getToken();
      if (!t) { navigate('/admin/login'); return; }
      try {
        const [d, me] = await Promise.all([
          axios.get(`${API}/api/sponsor/dashboard`, { headers: { Authorization: `Bearer ${t}` } }),
          axios.get(`${API}/api/sponsor/me`, { headers: { Authorization: `Bearer ${t}` } }),
        ]);
        setData(d.data); setRole(me.data.role);
      } catch (e) {
        setError(e?.response?.data?.detail || 'Accès refusé — connectez-vous avec un compte sponsor.');
      } finally { setLoading(false); }
    })();
  }, [navigate]);

  const logout = () => { clearToken(); navigate('/'); };

  return (
    <div className="min-h-screen bg-slate-950" data-testid="espace-sponsor-page">
      <header className="border-b border-slate-800 bg-slate-900/60 backdrop-blur sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-emerald-500/20 flex items-center justify-center"><Heart className="w-5 h-5 text-emerald-400" /></div>
            <span className="text-white font-bold">Espace Sponsor</span>
          </div>
          <button onClick={() => { logout(); window.location.href = '/'; }} className="text-slate-400 hover:text-white flex items-center gap-2 text-sm" data-testid="sponsor-logout">
            <LogOut className="w-4 h-4" /> Déconnexion
          </button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10">
        {loading && <div className="flex items-center gap-2 text-slate-400"><Loader2 className="w-5 h-5 animate-spin" /> Chargement…</div>}

        {!loading && error && (
          <div className="flex items-start gap-3 text-amber-300 bg-amber-500/10 rounded-lg p-4 max-w-lg" data-testid="sponsor-dashboard-error">
            <AlertTriangle className="w-5 h-5 mt-0.5" /><p>{error}</p>
          </div>
        )}

        {!loading && data && (
          <div className="space-y-8">
            <div>
              <p className="text-emerald-400 text-sm font-medium uppercase tracking-wider">Espace privé</p>
              <h1 className="text-white text-3xl font-bold mt-1" data-testid="sponsor-org-name">{data.organisation}</h1>
              <span className="inline-block mt-2 px-3 py-1 rounded-full bg-emerald-500/15 text-emerald-300 text-xs font-medium">{data.status}</span>
            </div>

            <nav className="flex flex-wrap gap-2 border-b border-slate-800 pb-1" data-testid="sponsor-tabs">
              {TABS.map((t) => {
                const Icon = t.icon;
                return (
                  <button key={t.id} onClick={() => setTab(t.id)} data-testid={`tab-${t.id}`}
                    className={`flex items-center gap-2 px-4 py-2 rounded-t-lg text-sm font-medium transition-colors ${tab === t.id ? 'bg-slate-900 text-emerald-300 border-b-2 border-emerald-400' : 'text-slate-400 hover:text-white'}`}>
                    <Icon className="w-4 h-4" />{t.label}
                  </button>
                );
              })}
            </nav>

            {tab === 'dashboard' && <DashboardTab data={data} />}
            {tab === 'convention' && <ConventionDocumentsTab role={role} />}
            {tab === 'contributions' && <ContributionsTab />}
            {tab === 'impact' && <ImpactTab />}
            {tab === 'programmes' && <ProgrammesTab />}
            {tab === 'reseau' && <ReseauTab />}
            {tab === 'evenements' && <EvenementsTab />}
            {tab === 'renouvellement' && <RenouvellementTab role={role} />}
          </div>
        )}
      </main>
    </div>
  );
}

function DashboardTab({ data }) {
  return (
    <div className="space-y-8" data-testid="dashboard-tab">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <InfoCard icon={<Building2 className="w-5 h-5" />} label="Catégorie" value={data.sponsor?.sponsor_category || '—'} testid="sc-categorie" />
        <InfoCard icon={<MapPin className="w-5 h-5" />} label="Territoires" value={(data.sponsor?.territories || []).join(', ') || '—'} testid="sc-territoires" />
        <InfoCard icon={<Layers className="w-5 h-5" />} label="Branches soutenues" value={(data.sponsor?.branches || []).join(', ') || '—'} testid="sc-branches" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <StatCard label="Contributions" value={data.contributions_count ?? 0} testid="sc-contributions" />
        <StatCard label="Documents" value={data.documents_count ?? 0} testid="sc-documents" />
      </div>
      <CollaborateursSection />
    </div>
  );
}

function InfoCard({ icon, label, value, testid }) {
  return (
    <div className="bg-slate-900 border border-slate-800 rounded-xl p-5" data-testid={testid}>
      <div className="flex items-center gap-2 text-emerald-400 mb-2">{icon}<span className="text-slate-400 text-sm">{label}</span></div>
      <p className="text-white font-semibold">{value}</p>
    </div>
  );
}

function StatCard({ label, value, testid }) {
  return (
    <div className="bg-gradient-to-br from-slate-900 to-slate-800 border border-slate-800 rounded-xl p-6" data-testid={testid}>
      <div className="text-3xl font-bold text-white">{value}</div>
      <div className="text-slate-400 text-sm mt-1">{label}</div>
    </div>
  );
}

function CollaborateursSection() {
  const [role, setRole] = useState(null);
  const [membres, setMembres] = useState([]);
  const [pending, setPending] = useState([]);
  const [email, setEmail] = useState('');
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);
  const auth = { Authorization: `Bearer ${getToken()}` };

  const load = async () => {
    try {
      const me = await axios.get(`${API}/api/sponsor/me`, { headers: auth });
      setRole(me.data.role);
      const c = await axios.get(`${API}/api/sponsor/collaborateurs`, { headers: auth });
      setMembres(c.data.membres || []);
      setPending(c.data.invitations_en_attente || []);
    } catch { /* ignore */ }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const invite = async () => {
    setMsg(''); setBusy(true);
    try {
      const r = await axios.post(`${API}/api/sponsor/collaborateurs/inviter`, { email }, { headers: auth });
      setMsg(`Invitation envoyée à ${r.data.email}.`);
      setEmail('');
      await load();
    } catch (e) {
      setMsg(e?.response?.data?.detail || "Échec de l'invitation.");
    } finally { setBusy(false); }
  };

  return (
    <div className="bg-slate-900 border border-slate-800 rounded-xl p-5" data-testid="collaborateurs-section">
      <div className="flex items-center gap-2 text-emerald-400 mb-4">
        <Users className="w-5 h-5" />
        <span className="text-white font-semibold">Collaborateurs de l'organisation</span>
      </div>
      <ul className="space-y-2 mb-4">
        {membres.map((m) => (
          <li key={m.id} className="bg-slate-800/60 rounded-lg px-3 py-2" data-testid={`collab-${m.email}`}>
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-200">{m.prenom} {m.nom} · {m.email}</span>
              <span className={`text-xs px-2 py-0.5 rounded ${m.role === 'sponsor_owner' ? 'bg-amber-600/30 text-amber-300' : 'bg-slate-700 text-slate-300'}`}>
                {m.role === 'sponsor_owner' ? 'Responsable' : 'Membre'}
              </span>
            </div>
            {role === 'sponsor_owner' && m.role === 'sponsor_member' && (
              <PermissionEditor member={m} auth={auth} onSaved={load} />
            )}
          </li>
        ))}
        {pending.map((p) => (
          <li key={p.email} className="flex items-center justify-between text-sm text-slate-400 px-3 py-1.5" data-testid={`collab-pending-${p.email}`}>
            <span>{p.email}</span><span className="text-xs italic">invitation en attente</span>
          </li>
        ))}
      </ul>
      {role === 'sponsor_owner' && (
        <div className="flex flex-col sm:flex-row gap-2">
          <input
            type="email" value={email} onChange={(e) => setEmail(e.target.value)}
            placeholder="email@collaborateur.fr" data-testid="collab-email-input"
            className="flex-1 bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm outline-none focus:border-emerald-500"
          />
          <button
            onClick={invite} disabled={busy || !email} data-testid="collab-invite-btn"
            className="bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors"
          >
            {busy ? 'Envoi…' : 'Inviter'}
          </button>
        </div>
      )}
      {msg && <p className="text-sm text-emerald-400 mt-2" data-testid="collab-msg">{msg}</p>}
    </div>
  );
}

const PERMISSIONS = [
  { v: 'DOCUMENTS', l: 'Documents' },
  { v: 'CONTRIBUTIONS', l: 'Contributions' },
  { v: 'IMPACT', l: 'Impact' },
  { v: 'PROGRAMMES', l: 'Programmes' },
  { v: 'NETWORK', l: 'Réseau' },
  { v: 'EVENTS', l: 'Événements' },
];

function PermissionEditor({ member, auth, onSaved }) {
  const [perms, setPerms] = useState(member.sponsor_permissions || []);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const toggle = (p) => setPerms((cur) => (cur.includes(p) ? cur.filter((x) => x !== p) : [...cur, p]));
  const save = async () => {
    setSaving(true); setSaved(false);
    try {
      await axios.patch(`${API}/api/sponsor/collaborateurs/${member.id}/permissions`, { permissions: perms }, { headers: auth });
      setSaved(true); onSaved && onSaved();
    } catch { /* ignore */ } finally { setSaving(false); }
  };

  return (
    <div className="mt-2 flex flex-wrap items-center gap-2" data-testid={`perm-editor-${member.email}`}>
      {PERMISSIONS.map((p) => (
        <button key={p.v} onClick={() => toggle(p.v)} data-testid={`perm-${member.email}-${p.v}`}
          className={`text-xs px-2 py-0.5 rounded border transition-colors ${perms.includes(p.v) ? 'bg-emerald-600/30 text-emerald-300 border-emerald-500/40' : 'bg-slate-900 text-slate-500 border-slate-700'}`}>
          {p.l}
        </button>
      ))}
      <button onClick={save} disabled={saving} data-testid={`perm-save-${member.email}`} className="text-xs text-emerald-400 hover:text-emerald-300 ml-1">
        {saving ? '…' : (saved ? '✓ enregistré' : 'Enregistrer')}
      </button>
    </div>
  );
}
